import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import {
  changeTableNumber,
  sendTableReadyMessage,
} from "../../actions/restaurantActions";
import { cancelledDidNotAttendStatuses } from "./..//ReservationStatus";
import { CustomModal, ModernButton } from "../CustomModal";

/* ---------- Styled ---------- */
const NumberBoxes = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  border: 1px solid gray;
  border-radius: 4px;
  background-color: white;
  color: black;
  margin: 5px;
  cursor: pointer;
  &:hover {
    background-color: #ededed;
  }
`;

const Circle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #99b0ad;
  color: #faf6e9;
  cursor: pointer;
  position: absolute;
  right: 3px;
  top: 38px;
  &:hover {
    background-color: #ededed;
  }
`;

function ReservationTableNameSelector({
  reservation,
  reservations,
  dispatch_reservation_wait = () => {},
}) {
  const dispatch = useDispatch();

  const [numbers] = useState(Array.from({ length: 101 }, (_, index) => index));

  const [showPickerModal, setShowPickerModal] = useState(false);

  /** SWAP confirm modal (reuses same modal for loading + success) */
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapPhase, setSwapPhase] = useState("confirm"); // 'confirm' | 'loading' | 'result'
  const [swapResultText, setSwapResultText] = useState("");

  /** Feedback modal for direct table change (uses same CustomModal component) */
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackPhase, setFeedbackPhase] = useState("loading"); // 'loading' | 'result'
  const [feedbackText, setFeedbackText] = useState("");

  /** “table ready” question (kept as Bootstrap Modal as in your code) */
  const [showSendTableReadyMessageModal, setShowSendTableReadyMessageModal] =
    useState(false);

  /** context for swap */
  const [reservationToChangeNumber, setReservationToChangeNumber] =
    useState("");
  const [reservationToChangeName, setReservationToChangeName] = useState("");
  const [reservationToChangeToken, setReservationToChangeToken] = useState("");

  const handleClosePicker = () => setShowPickerModal(false);

  const checkOverlap = (
    newStartTime,
    newEndTime,
    existingStartTime,
    existingEndTime
  ) => newStartTime <= existingEndTime && newEndTime >= existingStartTime;

  const markNotAllowedTables = () => {
    if (!reservations) return [];

    const baseDate =
      reservation?.date || new Date().toISOString().substring(0, 10);
    const existingStartTime = new Date(`${baseDate}T${reservation.time}:00`);
    const existingEndTime = new Date(
      `${baseDate}T${reservation.estimatedtime}:00`
    );

    const activeReservations = reservations.filter(
      (r) => !cancelledDidNotAttendStatuses.includes(r.status)
    );

    return numbers.map((number) => {
      let reservation_name;
      let reservation_token;

      const isNotAllowed = activeReservations.some((r) => {
        if (r.table_number === number.toString()) {
          const newStartTime = new Date(`${baseDate}T${r.time}:00`);
          const newEndTime = new Date(`${baseDate}T${r.estimatedtime}:00`);
          reservation_name = r.full_name;
          reservation_token = r.token;
          return checkOverlap(
            newStartTime,
            newEndTime,
            existingStartTime,
            existingEndTime
          );
        }
        return false;
      });

      return { number, isNotAllowed, reservation_name, reservation_token };
    });
  };

  /** Direct set (available number): show feedback modal -> loading -> result */
  const handleDirectSet = async (number) => {
    // if waiting list, also ask about table ready message (same as your logic)
    if (reservation.status === "waiting_list") {
      setShowSendTableReadyMessageModal(true);
    }

    // show same style modal as confirm: start in loading
    setFeedbackText("A processar alteração da mesa…");
    setFeedbackPhase("loading");
    setShowFeedbackModal(true);

    try {
      await dispatch(changeTableNumber(reservation.token, number));
      // success
      setFeedbackText(
        number === null
          ? "Mesa removida desta reserva com sucesso."
          : `Mesa alterada para ${number} com sucesso.`
      );
      setFeedbackPhase("result");
    } catch (e) {
      setFeedbackText(e.message || "Ocorreu um erro ao mudar a mesa.");
      setFeedbackPhase("result");
    }
  };

  /** Click on a number button */
  const handleChangeTable = (
    number,
    isNotAllowed,
    reservation_name,
    reservation_token
  ) => {
    if (!isNotAllowed) {
      setShowPickerModal(false);
      handleDirectSet(number);
    } else {
      // Launch swap confirm modal (will reuse for loading + result)
      setReservationToChangeNumber(number);
      setReservationToChangeToken(reservation_token);
      setReservationToChangeName(reservation_name);
      setSwapPhase("confirm");
      setShowSwapModal(true);
    }
  };

  /** Confirm swap: same modal transitions to loading -> result */
  const changeTablesWithOtherReservation = async () => {
    // if waiting list, also ask about table ready message
    if (reservation.status === "waiting_list") {
      setShowSendTableReadyMessageModal(true);
    }

    setSwapPhase("loading"); // button switches to "A processar…"
    try {
      await dispatch(
        changeTableNumber(
          reservation.token,
          reservationToChangeNumber,
          reservationToChangeToken
        )
      );
      setSwapResultText(
        `Mesas trocadas com sucesso (agora: ${reservationToChangeNumber}).`
      );
      setSwapPhase("result"); // show OK
    } catch (e) {
      setSwapResultText(e.message || "Ocorreu um erro ao trocar as mesas.");
      setSwapPhase("result"); // still show OK, but with error message
    }
  };

  const sendTheTableReadyMessage = () => {
    dispatch(sendTableReadyMessage(reservation.token));
    setShowSendTableReadyMessageModal(false);
  };

  return (
    <>
      <Circle
        onClick={() => {
          if (!cancelledDidNotAttendStatuses.includes(reservation.status)) {
            dispatch_reservation_wait();
            setShowPickerModal(true);
          }
        }}
        title="Clique para atribuir/trocar a mesa"
      >
        {reservation.table_number && reservation.table_number !== "null"
          ? reservation.table_number
          : ""}
      </Circle>

      {/* PICKER: choose number or blank */}
      <CustomModal
        show={showPickerModal}
        onHide={handleClosePicker}
        title="Nomear mesa"
        centered
        body={
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              height: "100%",
              width: "100%",
            }}
          >
            {/* Empty box = remove table */}
            <NumberBoxes
              title="Remover mesa"
              onClick={() => handleChangeTable(null)}
            />
            {markNotAllowedTables().map(
              ({
                number,
                isNotAllowed,
                reservation_name,
                reservation_token,
              }) => (
                <NumberBoxes
                  key={number}
                  onClick={() =>
                    handleChangeTable(
                      number,
                      isNotAllowed,
                      reservation_name,
                      reservation_token
                    )
                  }
                  style={{
                    backgroundColor:
                      parseInt(number) === parseInt(reservation.table_number)
                        ? "#9FBBC1"
                        : isNotAllowed
                        ? "#deb949"
                        : "#ffffff",
                  }}
                  title={
                    isNotAllowed && reservation_name
                      ? `Indisponível – reservado por ${reservation_name}`
                      : "Disponível"
                  }
                >
                  {number}
                </NumberBoxes>
              )
            )}
          </div>
        }
      />

      {/* SWAP confirm -> loading -> result (same modal instance) */}
      <CustomModal
        show={showSwapModal}
        onHide={() => setShowSwapModal(false)}
        title={
          swapPhase === "confirm"
            ? "Confirmar troca de mesa"
            : swapPhase === "loading"
            ? "A processar…"
            : "Resultado"
        }
        body={
          swapPhase === "confirm" ? (
            <>
              Trocar mesa <b>{reservation.table_number}</b> com mesa{" "}
              <b>{reservationToChangeNumber}</b> da reserva{" "}
              <b>{reservationToChangeName}</b>?
            </>
          ) : swapPhase === "loading" ? (
            <>A processar a troca de mesas…</>
          ) : (
            <>{swapResultText}</>
          )
        }
        footer={
          swapPhase === "confirm" ? (
            <ModernButton
              color="#EE754C"
              onClick={changeTablesWithOtherReservation}
            >
              Confirmar
            </ModernButton>
          ) : swapPhase === "loading" ? (
            <ModernButton color="#99B0AD" disabled>
              A processar…
            </ModernButton>
          ) : (
            <ModernButton
              color="#99B0AD"
              onClick={() => setShowSwapModal(false)}
            >
              OK
            </ModernButton>
          )
        }
      />

      {/* FEEDBACK for direct set: loading -> result (same style modal) */}
      <CustomModal
        show={showFeedbackModal}
        onHide={() => {
          if (feedbackPhase !== "loading") setShowFeedbackModal(false);
        }}
        title={feedbackPhase === "loading" ? "A processar…" : "Resultado"}
        body={<>{feedbackText}</>}
        footer={
          feedbackPhase === "loading" ? (
            <ModernButton color="#99B0AD" disabled>
              A processar…
            </ModernButton>
          ) : (
            <ModernButton
              color="#99B0AD"
              onClick={() => setShowFeedbackModal(false)}
            >
              OK
            </ModernButton>
          )
        }
      />

      {/* Table ready message (kept as-is) */}
      <Modal
        show={showSendTableReadyMessageModal}
        onHide={() => setShowSendTableReadyMessageModal(false)}
      >
        <div>
          <Modal.Header closeButton>
            <Modal.Title>Enviar mensagem</Modal.Title>
          </Modal.Header>
          <Modal.Body>Avisar cliente que a sua mesa está pronta?</Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={sendTheTableReadyMessage}>
              Enviar
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowSendTableReadyMessageModal(false)}
            >
              Não Enviar
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
}

export default ReservationTableNameSelector;
